"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BlocktankClient = exports.BlocktankClientError = void 0;
const axios_1 = __importDefault(require("axios"));
const ICreateOrderOptions_1 = require("./ICreateOrderOptions");
class BlocktankClientError extends Error {
    constructor(message, data) {
        super(`${message} ${JSON.stringify(data)}`);
        this.data = data;
        this.name = 'BlocktankClientError';
    }
}
exports.BlocktankClientError = BlocktankClientError;
/**
 * This class is used to communicate with the ln2 worker.
 */
class BlocktankClient {
    constructor(_baseUrl = 'https://blocktank.synonym.to/api/v2') {
        this._baseUrl = _baseUrl;
    }
    get baseUrl() {
        let url = this._baseUrl;
        if (this._baseUrl.endsWith('/')) {
            url = url.slice(0, url.length - 1);
        }
        return url;
    }
    set baseUrl(url) {
        this._baseUrl = url;
    }
    wrapErrorHandler(message, run) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return yield run();
            }
            catch (e) {
                throw new BlocktankClientError(message, (_a = e === null || e === void 0 ? void 0 : e.response) === null || _a === void 0 ? void 0 : _a.data);
            }
        });
    }
    /**
     * Get general service information.
     * @returns
     */
    getInfo() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.wrapErrorHandler('Failed to get info.', () => __awaiter(this, void 0, void 0, function* () {
                const response = yield axios_1.default.get(this.baseUrl + '/info');
                return response.data;
            }));
        });
    }
    /**
     * Estimates the fee to create a channel order without actually creating an order.
     * @param lspBalanceSat Number of satoshi that the LSP will provide on their channel side initially.
     * @param channelExpiryWeeks Number of weeks the channel will be leased for before the LSP may close the channel.
     * @param options Optional parameters.
     * @returns
     */
    estimateOrderFee(lspBalanceSat, channelExpiryWeeks, options = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const opts = Object.assign({}, ICreateOrderOptions_1.defaultCreateOrderOptions, options);
            return yield this.wrapErrorHandler(`Failed to estimate channel order fee.`, () => __awaiter(this, void 0, void 0, function* () {
                const response = yield axios_1.default.post(this.baseUrl + '/channels/estimate-fee', Object.assign(Object.assign({}, opts), { lspBalanceSat: lspBalanceSat, channelExpiryWeeks: channelExpiryWeeks }));
                return response.data;
            }));
        });
    }
    /**
 * Create a new channel order.
 * @param lspBalanceSat Number of satoshi that the LSP will provide on their channel side initially.
 * @param channelExpiryWeeks Number of weeks the channel will be leased for before the LSP may close the channel.
 * @param options Optional parameters.
 * @returns
 */
    createOrder(lspBalanceSat, channelExpiryWeeks, options = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const opts = Object.assign({}, ICreateOrderOptions_1.defaultCreateOrderOptions, options);
            // Small renaming because `zeroConf` is not clear.
            opts.zeroConf = !!opts.turboChannel;
            delete opts.turboChannel;
            return yield this.wrapErrorHandler(`Failed to create order.`, () => __awaiter(this, void 0, void 0, function* () {
                const response = yield axios_1.default.post(this.baseUrl + '/channels', Object.assign(Object.assign({}, opts), { lspBalanceSat: lspBalanceSat, channelExpiryWeeks: channelExpiryWeeks }));
                return response.data;
            }));
        });
    }
    /**
     * Get order by order id. Throws if it doesn't find the order.
     * @param orderId UUID
     * @returns
     */
    getOrder(orderId) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.wrapErrorHandler(`Failed to fetch order ${orderId}.`, () => __awaiter(this, void 0, void 0, function* () {
                const response = yield axios_1.default.get(this.baseUrl + '/channels/' + orderId);
                return response.data;
            }));
        });
    }
    /**
     * Get orders by multiple order ids. Throws if it doesn't find the orders.
     * @param orderIds UUID[]
     * @returns
     */
    getOrders(orderIds) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.wrapErrorHandler(`Failed to fetch orders ${JSON.stringify(orderIds)}.`, () => __awaiter(this, void 0, void 0, function* () {
                const response = yield axios_1.default.get(this.baseUrl + '/channels', {
                    params: {
                        ids: orderIds
                    }
                });
                return response.data;
            }));
        });
    }
    /**
     * Open channel to a specific node.
     * @param orderId
     * @param connectionStringOrPubkey
     * @param announceChannel
     * @returns
     */
    openChannel(orderId, connectionStringOrPubkey) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.wrapErrorHandler(`Failed to open the channel for order ${orderId}.`, () => __awaiter(this, void 0, void 0, function* () {
                const response = yield axios_1.default.post(this.baseUrl + `/channels/${orderId}/open`, {
                    connectionStringOrPubkey: connectionStringOrPubkey,
                });
                return response.data;
            }));
        });
    }
    getMin0ConfTxFee(orderId) {
        return __awaiter(this, void 0, void 0, function* () {
            const window = yield this.wrapErrorHandler(`Failed to fetch order ${orderId}.`, () => __awaiter(this, void 0, void 0, function* () {
                const response = yield axios_1.default.get(this.baseUrl + '/channels/' + orderId + '/min-0conf-tx-fee');
                return response.data;
            }));
            return window;
        });
    }
    /**
     * Create a new CJIT entry for Just-In-Time channel open.
     * @param channelSizeSat Number of satoshi the channel should be. Use getInfo to determine the possible sizes.
     * @param invoiceSat Number of satoshi of the created invoice. channelSizeSat must be at least 2 times bigger than invoiceSat.
     * @param invoiceDescription Description of the invoice.
     * @param nodeId Pubkey of the node to open the channel to.
     * @param channelExpiryWeeks How long the channel is leased for.
     * @param couponCode Optional couponCode to get discount and track the order.
     * @returns
     */
    createCJitEntry(channelSizeSat, invoiceSat, invoiceDescription, nodeId, channelExpiryWeeks, options = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.wrapErrorHandler(`Failed to create cjit entry.`, () => __awaiter(this, void 0, void 0, function* () {
                const response = yield axios_1.default.post(this.baseUrl + '/cjit', {
                    channelSizeSat,
                    invoiceSat,
                    invoiceDescription,
                    channelExpiryWeeks,
                    nodeId,
                    source: options.source,
                    discountCode: options.discountCode,
                });
                return response.data;
            }));
        });
    }
    /**
     * Get cjit entry by id. Throws if it doesn't find the entry.
     * @param entryId UUID
     * @returns
     */
    getCJitEntry(entryId) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.wrapErrorHandler(`Failed to fetch cjit entry ${entryId}.`, () => __awaiter(this, void 0, void 0, function* () {
                const response = yield axios_1.default.get(this.baseUrl + '/cjit/' + entryId);
                return response.data;
            }));
        });
    }
    /**
     * Sends a log message from Bitkit to Blocktank. Heavily rate limited.
     * @param nodeId Node id
     * @param message Log message. Max 1024 characters.
     * @returns Throws in case of rate limiting.
     */
    bitkitLog(nodeId, message) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.wrapErrorHandler(`Failed to send log for ${nodeId}.`, () => __awaiter(this, void 0, void 0, function* () {
                const response = yield axios_1.default.post(this.baseUrl + '/bitkit/log', {
                    nodeId,
                    message
                });
                return response.data;
            }));
        });
    }
}
exports.BlocktankClient = BlocktankClient;
//# sourceMappingURL=BlocktankClient.js.map